import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import dynamic from 'next/dynamic';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import cn from 'classnames';
import withStore from 'context/Store/withStore';
import { articles } from 'api';
import { useStateHandlers, useTranslation } from 'hooks';
import { defaultFilters, getSeoTitle, cacheTime } from 'utils';
import { Properties } from 'types';

const Container = dynamic(() => import('components/Grid/Container'));
const Row = dynamic(() => import('components/Grid/Row'));
const Col = dynamic(() => import('components/Grid/Col'));
const SearchResults = dynamic(() => import('components/SearchResults'));
const SearchPageInput = dynamic(() => import('components/SearchPageInput'));
const Section = dynamic(() => import('components/Layout/Section'));

const Search: NextPage = () => {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    query: { keyword: name_content_icontains },
  } = useRouter();

  const [state, setState] = useStateHandlers<Properties>({
    ...defaultFilters,
    name_content_icontains: '',
  });

  const { data, isLoading } = useQuery(['search', state], articles.getList, {
    enabled: !!state.name_content_icontains.length,
    cacheTime,
  });

  useEffect(() => {
    if (name_content_icontains) {
      setState({ name_content_icontains });
    }
  }, [name_content_icontains]);

  useEffect(() => {
    if (!state.name_content_icontains.length) {
      queryClient.resetQueries(['article']);
    }
  }, [state.name_content_icontains]);

  return (
    <>
      <Head>
        <title>{getSeoTitle(t('search'))}</title>
        <meta name="description" content={t('metaSearch')} />
      </Head>

      <Container className={cn('pt-20')}>
        <Row g={4}>
          <Col size={12}>
            <Section>
              <h1 className="section__title">{t('search')}</h1>
            </Section>
          </Col>
          <Col size={12}>
            <SearchPageInput
              setSearch={(name_content_icontains) => setState({ name_content_icontains, page: 1 })}
              value={router.query.keyword}
            />
          </Col>
          <Col size={12}>
            <SearchResults
              loading={isLoading}
              page={state.page}
              data={data}
              limit={state.page_size}
              search={state.name_content_icontains}
              setPage={(page) => setState({ page })}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default withStore()(Search);
